import { useState, useEffect } from 'react';
import { Eraser, Mail, User, LogIn, LogOut, Settings, ChevronDown, FolderOpen, Crown, MessageCircle, Inbox, Megaphone, Calendar, Zap } from 'lucide-react';
import { AudioPlayer } from './AudioPlayer';
import { VisitCounter } from './VisitCounter';
import { authService, roleService, sparksService } from '../lib/supabase';

interface HeaderProps {
  deckColors?: string[]; // For color-aware music
  onResetSession?: () => void;
  onOpenSettings?: () => void;
  onOpenAuth?: () => void;
  onOpenMyDecks?: () => void;
  onOpenMyEvents?: () => void;
  onOpenAdminPanel?: () => void;
  onOpenModMessage?: () => void;
  onOpenInbox?: () => void;
  onOpenFeedback?: () => void;
  onNavigateToLanding?: () => void;
}

export function Header({
  deckColors = [],
  onResetSession,
  onOpenSettings,
  onOpenAuth,
  onOpenMyDecks,
  onOpenMyEvents,
  onOpenAdminPanel,
  onOpenModMessage,
  onOpenInbox,
  onOpenFeedback,
  onNavigateToLanding
}: HeaderProps) {
  const [user, setUser] = useState<any>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showFeedbackMenu, setShowFeedbackMenu] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMod, setIsMod] = useState(false);
  const [sparkBalance, setSparkBalance] = useState<number>(0);
  const [canClaimDaily, setCanClaimDaily] = useState(false);
  const [claimingDaily, setClaimingDaily] = useState(false);
  const [dailyClaimed, setDailyClaimed] = useState(false);
  const [showEarnInfo, setShowEarnInfo] = useState(false);

  useEffect(() => {
    authService.getCurrentUser().then(setUser);
    const { data: { subscription } } = authService.onAuthStateChange(setUser);
    return () => subscription.unsubscribe();
  }, []);

  // Check if user is admin or mod (staff)

  useEffect(() => {
    if (user) {
      roleService.hasRole('admin').then(setIsAdmin);
      roleService.hasRole('mod').then(setIsMod);
      // Load spark balance
      sparksService.getBalance(user.id).then(setSparkBalance);
      sparksService.canClaimDaily(user.id).then(setCanClaimDaily);
    } else {
      setIsAdmin(false);
      setIsMod(false);
      setSparkBalance(0);
      setCanClaimDaily(false);
    }
  }, [user]);

  const handleClaimDaily = async () => {
    if (!user || claimingDaily) return;
    setClaimingDaily(true);
    try {
      const result = await sparksService.claimDailySignIn(user.id);
      if (result.success && result.balance !== undefined) {
        setSparkBalance(result.balance);
        setCanClaimDaily(false);
        setDailyClaimed(true);
      }
    } catch (err) {
      console.error('Failed to claim daily:', err);
    } finally {
      setClaimingDaily(false);
    }
  };

  const handleSignOut = async () => {
    await authService.signOut();
    setShowUserMenu(false);
  };

  return (
    <header className="border-b border-purple-500/20 bg-gradient-to-r from-[#050510] via-[#0a0a1a] to-[#050510] backdrop-blur-sm sticky top-0 z-50 shadow-lg shadow-purple-500/5">
      <div className="px-2 sm:px-4 py-1">
        <div className="flex items-center justify-between gap-2">
          {/* Left: DECKSTR.GG Logo */}
          <button
            onClick={onNavigateToLanding}
            className="flex items-center gap-1 sm:gap-2 flex-shrink-0 text-left cursor-pointer hover:opacity-90 transition-opacity"
            title="Return to DECKSTR Home"
          >
            {/* Animated DECKSTR Mascot */}
            <video
              src="/jud.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="h-8 w-8 sm:h-12 sm:w-12 object-contain mix-blend-lighten"
              aria-label="DECKSTR mascot"
            />
            {/* Image Logo */}
            <img
              src="/deckstr-logo.png"
              alt="DECKSTR.GG"
              className="h-8 sm:h-12 object-contain"
            />
          </button>

          {/* Center: Visit Counter — hidden on very small screens */}
          <div className="hidden sm:flex items-center justify-center flex-shrink-0">
            <VisitCounter />
          </div>

          {/* Right: Nav Dropdown + User Auth + Feedback buttons */}
          <div className="flex-1 flex justify-end items-center gap-1">
            {/* Portal target for NavDropdown — rendered by AnalyticsPanel */}
            <div id="header-nav-slot" className="relative" />
            {/* User Auth */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-1.5 px-2 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <User className="size-4 text-amber-400" />
                  <span className="text-sm text-white/80 hidden sm:inline max-w-[80px] truncate">
                    {user.user_metadata?.display_name || user.email?.split('@')[0]}
                  </span>
                  {/* Spark Balance Badge */}
                  <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-500/15 rounded text-xs font-semibold text-amber-400 border border-amber-500/20" title={`${sparkBalance} Sparks`}>
                    <img src="/spark-icon.png" alt="Sparks" className="w-5 h-5 -my-0.5" />
                    {sparkBalance}
                  </span>
                  <ChevronDown className="size-3 text-white/40" />
                </button>

                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 w-52 bg-slate-900 border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden">
                      <div className="px-3 py-2 border-b border-white/10">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-white font-medium truncate">
                            {user.user_metadata?.display_name || 'User'}
                          </p>
                          <span className="flex items-center gap-1 text-xs font-bold text-amber-400">
                            <img src="/spark-icon.png" alt="" className="w-5 h-5" />
                            {sparkBalance}
                          </span>
                        </div>
                        <p className="text-xs text-white/40 truncate">{user.email}</p>
                      </div>
                      {/* Daily Sign-In Claim */}
                      {canClaimDaily ? (
                        <button
                          onClick={handleClaimDaily}
                          disabled={claimingDaily}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-400 hover:bg-amber-500/10 transition-colors border-b border-white/5 disabled:opacity-50"
                        >
                          <img src="/spark-icon.png" alt="" className="w-5 h-5" />
                          {claimingDaily ? 'Claiming...' : 'Claim Daily +1 ⚡'}
                        </button>
                      ) : (
                        <div className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-400/60 border-b border-white/5">
                          <img src="/spark-icon.png" alt="" className="w-5 h-5 opacity-50" />
                          {dailyClaimed ? 'Claimed! ✅' : 'Daily Spark — come back tomorrow!'}
                        </div>
                      )}
                      <button
                        onClick={() => setShowEarnInfo(!showEarnInfo)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-purple-400/70 hover:bg-purple-500/10 hover:text-purple-400 transition-colors"
                      >
                        <Zap className="size-4" />
                        Get More Sparks
                        <ChevronDown className={`size-3 ml-auto transition-transform ${showEarnInfo ? 'rotate-180' : ''}`} />
                      </button>
                      {showEarnInfo && (
                        <div className="px-3 py-2 bg-purple-500/5 border-y border-white/5 space-y-1.5">
                          <p className="text-[10px] text-purple-400/60 font-semibold uppercase tracking-wider">Ways to Earn</p>
                          <div className="text-[11px] text-white/50 space-y-1">
                            <p>⚡ <span className="text-amber-400/70">Daily Login</span> — +1–7 sparks/day</p>
                            <p>💾 <span className="text-cyan-400/70">Save a Deck</span> — +2 sparks</p>
                            <p>👍 <span className="text-green-400/70">Vote on Decks</span> — every 20 votes = +1</p>
                            <p>🎲 <span className="text-orange-400/70">First Brewlette</span> — +5 sparks</p>
                            <p>🤝 <span className="text-pink-400/70">Add Friends</span> — milestone bonuses</p>
                            <p>📺 <span className="text-purple-400/70">Watch Streams</span> — via Twitch extension</p>
                          </div>
                        </div>
                      )}
                      {onOpenMyDecks && (
                        <button
                          onClick={() => { onOpenMyDecks(); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors"
                        >
                          <FolderOpen className="size-4" />
                          My Decks
                        </button>
                      )}
                      {onOpenMyEvents && (
                        <button
                          onClick={() => { onOpenMyEvents(); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-purple-400/70 hover:bg-purple-500/10 hover:text-purple-400 transition-colors"
                        >
                          <Calendar className="size-4" />
                          My Events
                        </button>
                      )}
                      {(isAdmin || isMod) && onOpenAdminPanel && (
                        <button
                          onClick={() => { onOpenAdminPanel(); setShowUserMenu(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isAdmin ? 'text-red-400/70 hover:bg-red-500/10 hover:text-red-400' : 'text-purple-400/70 hover:bg-purple-500/10 hover:text-purple-400'}`}
                        >
                          <Crown className="size-4" />
                          {isAdmin ? 'Admin Panel' : 'Staff Panel'}
                        </button>
                      )}
                      {onOpenSettings && (
                        <button
                          onClick={() => { onOpenSettings(); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors"
                        >
                          <Settings className="size-4" />
                          Settings
                        </button>
                      )}
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                      >
                        <LogOut className="size-4" />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={onOpenAuth}
                className="flex items-center gap-1.5 px-2 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-amber-400 rounded-lg text-sm transition-colors"
              >
                <LogIn className="size-4" />
                <span className="hidden sm:inline">Sign In</span>
              </button>
            )}

            <div className="relative">
              <button
                onClick={() => setShowFeedbackMenu(!showFeedbackMenu)}
                className="p-1.5 hover:bg-purple-500/20 text-white/40 hover:text-purple-400 rounded transition-colors"
                title="Send Feedback"
              >
                <Mail className="size-5" />
              </button>

              {showFeedbackMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowFeedbackMenu(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 w-56 bg-slate-900 border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden">
                    {onOpenFeedback && (
                      <button
                        onClick={() => { onOpenFeedback(); setShowFeedbackMenu(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors"
                      >
                        <Megaphone className="size-4" />
                        Leave Feedback
                      </button>
                    )}
                    {user && onOpenInbox && (
                      <button
                        onClick={() => { onOpenInbox(); setShowFeedbackMenu(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-purple-400/80 hover:bg-purple-500/10 hover:text-purple-400 transition-colors"
                      >
                        <Inbox className="size-4" />
                        My Messages
                      </button>
                    )}
                    {user && onOpenModMessage && (
                      <button
                        onClick={() => { onOpenModMessage(); setShowFeedbackMenu(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors"
                      >
                        <MessageCircle className="size-4" />
                        Message Mod Team
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
